/**
 * UploadManagerContext — iter805
 * ─────────────────────────────────────────────────────────────
 * Global, app-root-mounted tracker for the athlete's own video
 * uploads. Exists because a video upload used to be tracked purely in
 * whichever page's local state started it (a `useRef`/`useState` on
 * AthleteGameFolderPage) — the instant the athlete navigated away or
 * switched tabs, that component unmounted and the upload effectively
 * became invisible: the actual bytes kept flowing to Mux just fine
 * (a JS Promise isn't tied to a React component's lifecycle), but
 * nothing was left mounted to receive its progress or completion, so
 * it just seemed to vanish.
 *
 * Mounting this ONE level above the router (see App.js) means the
 * upload's own network calls live here instead — switching screens no
 * longer touches this component at all, so the upload keeps running
 * and reporting progress exactly as if nothing happened.
 *
 * `startVideoUpload` still performs the real init+PUT itself (single
 * source of truth for the actual network calls, so this never
 * duplicates the upload a calling page might also want to track more
 * richly via its own optimistic tile — see `onInit`/`onSettled`).
 *
 * A backend poll (`GET /me/media/uploads/active`) reconciles this
 * local list against the athlete's OWN durable pending-upload records
 * every few seconds — that's what makes an upload started in a
 * previous page load, or even a different device, still show up in
 * the global bar, and what makes a completed/failed upload disappear
 * from it once the backend confirms it's no longer in flight.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as UpChunk from '@mux/upchunk';
import { toast } from 'sonner';
import { useUserAuth } from './UserAuthContext';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// iter819 · No more raw axios PUT here (see startVideoUpload below,
// now on UpChunk) — UpChunk drives its own XHR internally, never
// touching the shared axios instance, so the "strip the global
// interceptor's Bearer token" workaround every other direct-to-Mux/
// Cloudinary upload path in this app still needs doesn't apply here
// anymore.
const UploadManagerCtx = createContext({
  activeUploads: [],
  startVideoUpload: async () => {},
});
const POLL_MS = 4000;
export function UploadManagerProvider({ children }) {
  const { user } = useUserAuth();
  const [activeUploads, setActiveUploads] = useState([]);
  const updateUpload = useCallback((key, patch) => {
    setActiveUploads((prev) => prev.map((u) => (u.key === key ? { ...u, ...patch } : u)));
  }, []);
  const removeUpload = useCallback((key) => {
    setActiveUploads((prev) => prev.filter((u) => u.key !== key));
  }, []);
  // `onInit(uploadId)` fires the moment the Mux upload URL is minted —
  // callers (e.g. AthleteGameFolderPage) use it to drop their own rich
  // optimistic tile (local file preview/poster) keyed to the same
  // upload id, without this context needing to know anything about
  // page-specific UI. `onSettled(ok)` fires once the byte PUT itself
  // finishes or fails — NOT once Mux has finished encoding, which this
  // context can't observe directly (that's what the backend poll below
  // is for).
  const startVideoUpload = useCallback(async (file, { eventId, eventTitle, onInit, onSettled } = {}) => {
    const key = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setActiveUploads((prev) => [...prev, {
      key, eventId, eventTitle: eventTitle || 'your folder',
      filename: file?.name || null, progress: 0, status: 'uploading', muxUploadId: null,
    }]);
    try {
      const { data: init } = await axios.post(
        `${API}/me/media/events/${eventId}/mux-upload-init`, {}, { withCredentials: true, timeout: 30_000 },
      );
      if (!init?.upload_url || !init?.upload_id) throw new Error('Video upload could not start — no upload URL returned.');
      updateUpload(key, { muxUploadId: init.upload_id });
      onInit?.(init.upload_id);
      // iter819 · Mux's own `upchunk` library (built for exactly this —
      // large browser video uploads) splits the file into 5MB chunks
      // and retries a failed chunk instead of the whole upload failing
      // on one dropped packet over a real mobile connection, and
      // reports real incremental progress instead of one big request's
      // rough estimate.
      await new Promise((resolve, reject) => {
        const upload = UpChunk.createUpload({
          endpoint: init.upload_url,
          file,
          chunkSize: 5120, // 5MB chunks, in KB
        });
        upload.on('error', (err) => {
          reject(new Error(err?.detail?.message || 'Video upload failed.'));
        });
        upload.on('progress', (evt) => {
          const pct = Number(evt?.detail);
          if (Number.isFinite(pct)) updateUpload(key, { progress: Math.min(99, Math.round(pct)) });
        });
        upload.on('success', () => resolve());
      });
      // Bytes are up — Mux is encoding now. Stay visible as "processing"
      // until the backend poll confirms it's actually done (its real
      // asset then shows up through the folder's own normal fetch).
      updateUpload(key, { progress: 100, status: 'processing' });
      onSettled?.(true);
    } catch (err) {
      removeUpload(key);
      toast.error(err?.response?.data?.detail || err?.message || 'Video upload failed.');
      onSettled?.(false);
    }
  }, [updateUpload, removeUpload]);
  // iter817 · Jay: a failed upload used to just disappear from this list
  // the instant the backend stopped returning it as "uploading" — no
  // failure ever reached the athlete, it just silently vanished. The
  // backend now also returns a recently-failed row (see
  // /me/media/uploads/active), so this needs to (a) keep a matching
  // local entry instead of dropping it once the server says "failed",
  // and (b) actually copy that status across — `kept` used to leave a
  // local tile's status exactly as it was pre-poll, never learning the
  // server's own verdict.
  const pollBackend = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/me/media/uploads/active`, { withCredentials: true });
      const serverUploads = data?.uploads || [];
      const serverById = new Map(serverUploads.map((s) => [s.mux_upload_id, s]));
      setActiveUploads((prev) => {
        const kept = prev
          .filter((u) => !u.muxUploadId || serverById.has(u.muxUploadId))
          .map((u) => {
            const server = u.muxUploadId ? serverById.get(u.muxUploadId) : null;
            return server && server.status === 'failed' ? { ...u, status: 'failed' } : u;
          });
        const localIds = new Set(kept.filter((u) => u.muxUploadId).map((u) => u.muxUploadId));
        const additions = serverUploads
          .filter((s) => !localIds.has(s.mux_upload_id))
          .map((s) => ({
            key: `server-${s.mux_upload_id}`,
            eventId: s.event_id,
            eventTitle: s.event_title,
            muxUploadId: s.mux_upload_id,
            filename: null,
            progress: 100,
            status: s.status === 'failed' ? 'failed' : 'processing',
          }));
        return [...kept, ...additions];
      });
    } catch { /* transient — retry next tick */ }
  }, [user]);
  const pollRef = useRef(pollBackend);
  pollRef.current = pollBackend;
  useEffect(() => {
    if (!user) { setActiveUploads([]); return undefined; }
    pollRef.current();
    const iv = setInterval(() => pollRef.current(), POLL_MS);
    return () => clearInterval(iv);
  }, [user]);
  return (
    <UploadManagerCtx.Provider value={{ activeUploads, startVideoUpload }}>
      {children}
    </UploadManagerCtx.Provider>
  );
}
export function useUploadManager() {
  return useContext(UploadManagerCtx);
}
